import { useInfiniteQuery, UseInfiniteQueryOptions } from 'react-query';

import { fetchJson } from '@zapier/toolbox-browser-fetch';

import { ZAPIER_APP_BASE_URL } from 'app/common/constants';

import { ZapTemplate } from 'app/zap-templates/types';
import { useActiveProfile, useNotify } from 'app/developer-v3/hooks';

const LIMIT_QUERY_LIST = 300;

const QUERY_KEY_PREFIX = 'query-zap-templates';
const getQueryKey = (activeProfileId: number) =>
  `${QUERY_KEY_PREFIX}-${activeProfileId}`;

function formatResponseBody(
  body: any
): {
  count: number;
  next: string;
  zapTemplates: ZapTemplate[];
} {
  const { count, next, results } = body;

  const zapTemplates = (results ?? []).map((zapTemplate: any) => ({
    headService: {
      image: zapTemplate.head_service?.image,
      image16x16: zapTemplate.head_service?.images.url_16x16,
      image32x32: zapTemplate.head_service?.images.url_32x32,
      name: zapTemplate.head_service?.name,
      slug: zapTemplate.head_service?.slug,
    },
    id: zapTemplate.id,
    slug: zapTemplate.slug,
    status: zapTemplate.status,
    title: zapTemplate.title,
    tailService: {
      image: zapTemplate.tail_service?.image,
      image16x16: zapTemplate.tail_service?.images.url_16x16,
      image32x32: zapTemplate.tail_service?.images.url_32x32,
      name: zapTemplate.tail_service?.name,
      slug: zapTemplate.tail_service?.slug,
    },
    guidedRecipeUrl: zapTemplate.guided_recipe_url,
  }));

  return { count, zapTemplates, next };
}

function useQueryZapTemplatesAll(
  options?: UseInfiniteQueryOptions<
    { count: number; data: ZapTemplate[]; next: string | null },
    Error
  >
) {
  const notify = useNotify();
  const {
    activeProfile,
    isLoading: isLoadingActiveProfile,
  } = useActiveProfile();

  const queryKey = getQueryKey(activeProfile?.id);
  const url = new URL('/api/v4/guided-recipes/', ZAPIER_APP_BASE_URL);
  if (!isLoadingActiveProfile && activeProfile?.id) {
    url.search = new URLSearchParams({
      added_by_id: String(activeProfile.id),
      exclude_unranked: 'false',
      include_unpublished: 'true',
      limit: String(LIMIT_QUERY_LIST),
      must_match_all_services: 'false',
      offset: String(0),
    }).toString();
  }

  const query = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = url.href }) => {
      const { count, zapTemplates, next } = formatResponseBody(
        await fetchJson(pageParam)
      );
      if (next) {
        const nextUrl = new URL('/api/v4/guided-recipes/', ZAPIER_APP_BASE_URL);
        nextUrl.search = new URL(next).search;
        return { count, data: zapTemplates, next: nextUrl.href };
      }
      return { count, data: zapTemplates, next: undefined };
    },
    getNextPageParam: (lastPage, _pages) => lastPage.next,
    onError: async (_error: any) =>
      notify.failure('Failed to fetch Zap templates.', queryKey),
    ...options,
    enabled:
      !!(options?.enabled ?? true) &&
      !isLoadingActiveProfile &&
      !!activeProfile?.id,
  });

  const { data, ...rest } = query;

  const { hasNextPage, fetchNextPage, isLoading } = rest;

  if (!isLoading && hasNextPage) {
    fetchNextPage();
  }

  return {
    ...rest,
    data,
    isLoading: rest.isLoading || rest.isRefetching,
    queryKey,
  };
}

export default useQueryZapTemplatesAll;
export { getQueryKey, QUERY_KEY_PREFIX };
